/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useState } from 'react';
import { supabase } from '../supabase';
import { SlideOverPanel, SecondaryBtn } from './systemKit';
import { runParseGuardrails } from '../lib/parseGuardrails';

// ─── Invoice Detail Drawer ──────────────────────────────────────────────────
// Slide-over from the right when an operator clicks an invoice row in the
// Invoices browser. Shows:
//   - Header (invoice #, vendor, date)
//   - Summary (total + line count)
//   - Line items table (item / qty / $/case / line total)
//   - Action buttons: View raw invoice (when file_url present) + Re-run parser
//   - Vendor comparison strip — "this invoice is X% above/below your avg
//     from this vendor" with a driver sentence ("Driver: Beef Chuck $/lb up
//     4% on this delivery"). Suppressed when the vendor has < 3 prior
//     invoices (sample too small for meaningful comparison).
//
// See DESIGN_SYSTEM.md §Invoices surface.

function fmtMoney(n) {
  if (n == null || !isFinite(n)) return '$0.00';
  return `$${Number(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function fmtMoneyShort(n) {
  if (n == null || !isFinite(n)) return '$0';
  if (Math.abs(n) >= 1000) return `$${(n / 1000).toFixed(1).replace(/\.0$/, '')}k`;
  return `$${Math.round(n).toLocaleString('en-US')}`;
}

function fmtDate(iso) {
  if (!iso) return '';
  return new Date(iso + 'T00:00:00').toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
}

function fmtVendor(v) {
  return v ? String(v).replace(/\n/g, ' \u00B7 ') : '\u2014';
}

const VENDOR_COMPARE_THRESHOLD = 0.10;        // ±10% from running avg
const VENDOR_COMPARE_MIN_PRIOR_INVOICES = 3;  // suppress strip below this

export default function InvoiceDetailDrawer({ open, onClose, companyId, invoice, onParserToast }) {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  // { invoiceLines, vendorPriorInvoices, vendorPriorLineMap, foodItemsById }
  const [parserRunning, setParserRunning] = useState(false);

  useEffect(() => {
    if (!open || !invoice || !companyId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      // Last 12 months of invoices from this same vendor (for comparison).
      const cutoffDate = new Date(invoice.invoice_date + 'T00:00:00');
      cutoffDate.setFullYear(cutoffDate.getFullYear() - 1);
      const cutoffIso = cutoffDate.toISOString().split('T')[0];

      // Fetch in parallel:
      //   1. This invoice's line items (with item normalized_name + flag state)
      //   2. Vendor's prior invoices (for running avg + comparison)
      //   3. Vendor's prior line items (for driver-line computation)
      //   4. Food items mapping (for item names + flags)
      const [linesRes, vendorInvRes, vendorLinesRes, itemsRes] = await Promise.all([
        supabase.from('invoice_line_items')
          .select('id, food_item_id, raw_description, normalized_name, quantity, unit, unit_price, line_total, total_weight, price_per_lb, pack_count, piece_weight, needs_review')
          .eq('company_id', companyId)
          .eq('invoice_id', invoice.id)
          .range(0, 999),
        supabase.from('invoices')
          .select('id, invoice_date, total_amount, invoice_number')
          .eq('company_id', companyId)
          .eq('vendor_name', invoice.vendor_name)
          .gte('invoice_date', cutoffIso)
          .lt('invoice_date', invoice.invoice_date)
          .order('invoice_date', { ascending: false })
          .range(0, 999),
        supabase.from('invoice_line_items')
          .select('invoice_id, food_item_id, line_total, price_per_lb, total_weight')
          .eq('company_id', companyId)
          .eq('vendor_name', invoice.vendor_name)
          .gte('invoice_date', cutoffIso)
          .lt('invoice_date', invoice.invoice_date)
          .range(0, 49999),
        supabase.from('food_items')
          .select('id, normalized_name, category, is_catch_weight, needs_review')
          .eq('company_id', companyId)
          .range(0, 9999),
      ]);
      if (cancelled) return;

      const itemsById = {};
      (itemsRes.data || []).forEach(it => { itemsById[it.id] = it; });

      // Build map: food_item_id → array of { line_total, price_per_lb }
      // from vendor's prior invoices. Used to compute the "driver" — the
      // line whose contribution to the current-invoice delta is largest.
      const vendorPriorLineMap = {};
      for (const l of (vendorLinesRes.data || [])) {
        if (l.food_item_id == null) continue;
        if (!vendorPriorLineMap[l.food_item_id]) vendorPriorLineMap[l.food_item_id] = [];
        vendorPriorLineMap[l.food_item_id].push(l);
      }

      setData({
        lines: linesRes.data || [],
        vendorPriorInvoices: vendorInvRes.data || [],
        vendorPriorLineMap,
        itemsById,
      });
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [open, invoice, companyId]);

  // ── Vendor comparison computation ──────────────────────────────────────
  const vendorComparison = useMemo(() => {
    if (!data || !invoice) return null;
    const priorInvoices = data.vendorPriorInvoices || [];
    const totalCount = priorInvoices.length;
    if (totalCount < VENDOR_COMPARE_MIN_PRIOR_INVOICES) {
      return {
        kind: 'insufficient',
        priorCount: totalCount,
      };
    }
    const avg = priorInvoices.reduce((s, i) => s + (Number(i.total_amount) || 0), 0) / totalCount;
    const curr = Number(invoice.total_amount) || 0;
    const deltaPct = avg > 0 ? ((curr - avg) / avg) : 0;
    const within = Math.abs(deltaPct) < VENDOR_COMPARE_THRESHOLD;

    if (within) {
      return {
        kind: 'within',
        avg,
        priorCount: totalCount,
      };
    }

    // Outside threshold — compute the driver line item.
    // For each line on THIS invoice, compute its average line_total in the
    // same vendor's prior invoices when the same food_item_id appeared.
    // The driver is the line with the largest absolute (current - avg)
    // contribution in dollar terms.
    let driver = null;
    let driverDelta = 0;
    for (const l of (data.lines || [])) {
      if (l.food_item_id == null) continue;
      const priorLines = data.vendorPriorLineMap[l.food_item_id] || [];
      if (priorLines.length === 0) continue;
      const priorAvg = priorLines.reduce((s, p) => s + (Number(p.line_total) || 0), 0) / priorLines.length;
      const lineDelta = (Number(l.line_total) || 0) - priorAvg;
      if (Math.abs(lineDelta) > Math.abs(driverDelta)) {
        driverDelta = lineDelta;
        driver = {
          itemName: data.itemsById[l.food_item_id]?.normalized_name || l.normalized_name || l.raw_description || 'Item',
          delta: lineDelta,
          currentTotal: Number(l.line_total) || 0,
          priorAvg,
          priorPerLb: priorLines.reduce((s, p) => s + (Number(p.price_per_lb) || 0), 0) / Math.max(1, priorLines.filter(p => p.price_per_lb != null).length),
          currentPerLb: Number(l.price_per_lb) || null,
        };
      }
    }

    return {
      kind: 'compared',
      avg,
      priorCount: totalCount,
      deltaPct,
      driver,
    };
  }, [data, invoice]);

  // ── Re-run parser handler ──────────────────────────────────────────────
  async function handleRerunParser() {
    if (!data || !companyId) return;
    setParserRunning(true);
    try {
      // Collect distinct food_item_ids from this invoice's lines.
      const ids = Array.from(new Set((data.lines || []).map(l => l.food_item_id).filter(v => v != null)));
      if (ids.length === 0) {
        onParserToast?.({ kind: 'info', text: 'No items on this invoice to re-check.' });
        return;
      }
      const result = await runParseGuardrails({ companyId, foodItemIds: ids });
      // runParseGuardrails returns { checked, flagged, by_layer, wouldFire }
      // where `flagged` is the count of items currently flagged AFTER the
      // run (not "newly flagged in this run" — the function can't
      // distinguish that without a snapshot taken before invocation).
      // Toast describes the post-state honestly.
      const checked = Number(result?.checked) || 0;
      const flagged = Number(result?.flagged) || 0;
      let text;
      let kind;
      if (checked === 0) {
        text = 'Parse guardrails complete \u2014 nothing to check.';
        kind = 'info';
      } else if (flagged === 0) {
        text = `Parse guardrails complete \u2014 ${checked} item${checked === 1 ? '' : 's'} checked, no flags.`;
        kind = 'success';
      } else {
        text = `Parse guardrails complete \u2014 ${flagged} of ${checked} item${checked === 1 ? '' : 's'} flagged for review.`;
        kind = 'warning';
      }
      onParserToast?.({ kind, text });
    } catch (err) {
      onParserToast?.({ kind: 'critical', text: `Parse guardrails failed: ${err.message || 'unknown error'}` });
    } finally {
      setParserRunning(false);
    }
  }

  if (!invoice) return null;

  const subtitle = (
    <span className="inline-flex items-center gap-2 flex-wrap">
      <span>{fmtVendor(invoice.vendor_name)}</span>
      <span style={{ color: 'var(--text-muted)' }}>{'\u00B7'}</span>
      <span>{fmtDate(invoice.invoice_date)}</span>
    </span>
  );

  return (
    <SlideOverPanel
      open={open}
      onClose={onClose}
      title={invoice.invoice_number ? `Invoice ${invoice.invoice_number}` : 'Invoice'}
      subtitle={subtitle}
      widthPx={520}
    >
      {loading || !data ? (
        <div className="p-5 text-sm" style={{ color: 'var(--text-muted)' }}>{'Loading\u2026'}</div>
      ) : (
        <div className="p-5 space-y-5">
          {/* ── Summary ───────────────────────────────────────────────────── */}
          <div>
            <div className="text-2xl font-semibold" style={{ color: 'var(--text-primary)', fontVariantNumeric: 'tabular-nums', letterSpacing: '-0.01em' }}>
              {fmtMoney(invoice.total_amount)}
            </div>
            <div className="text-sm mt-0.5" style={{ color: 'var(--text-secondary)' }}>
              {`${data.lines.length} line item${data.lines.length === 1 ? '' : 's'}`}
            </div>
          </div>

          {/* ── Line items ────────────────────────────────────────────────── */}
          <div>
            <div style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text-secondary)', marginBottom: 8 }}>
              Line items
            </div>
            <div className="rounded-lg overflow-hidden" style={{ border: '1px solid var(--border)' }}>
              <div className="grid text-xs" style={{
                gridTemplateColumns: '1fr 50px 80px 80px',
                background: 'var(--bg-subtle)', borderBottom: '1px solid var(--border)',
              }}>
                {[
                  { label: 'Item', align: 'left' },
                  { label: 'Qty', align: 'right' },
                  { label: '$/case', align: 'right' },
                  { label: 'Total', align: 'right' },
                ].map(h => (
                  <div key={h.label} className="px-3 py-2"
                    style={{
                      color: 'var(--text-secondary)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em',
                      textAlign: h.align,
                    }}
                  >{h.label}</div>
                ))}
              </div>
              {data.lines.map((l, i) => {
                const item = data.itemsById[l.food_item_id];
                const itemName = item?.normalized_name || l.normalized_name || l.raw_description || 'Unknown';
                return (
                  <div key={l.id} className="grid text-xs" style={{
                    gridTemplateColumns: '1fr 50px 80px 80px',
                    borderBottom: i < data.lines.length - 1 ? '1px solid var(--border)' : 'none',
                    minHeight: 36,
                    alignItems: 'center',
                  }}>
                    <div className="px-3 py-2 truncate" style={{ color: 'var(--text-primary)' }} title={itemName}>
                      {itemName}
                    </div>
                    <div className="px-3 py-2" style={{ color: 'var(--text-primary)', fontVariantNumeric: 'tabular-nums', textAlign: 'right' }}>
                      {Number(l.quantity || 0)}
                    </div>
                    <div className="px-3 py-2" style={{ color: 'var(--text-primary)', fontVariantNumeric: 'tabular-nums', textAlign: 'right' }}>
                      {l.unit_price != null ? `$${Number(l.unit_price).toFixed(2)}` : '\u2014'}
                    </div>
                    <div className="px-3 py-2" style={{ color: 'var(--text-primary)', fontVariantNumeric: 'tabular-nums', fontWeight: 500, textAlign: 'right' }}>
                      {l.line_total != null ? fmtMoney(l.line_total) : '\u2014'}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* ── Action buttons ────────────────────────────────────────────── */}
          <div className="flex items-center gap-2 flex-wrap">
            {invoice.file_url && (
              <SecondaryBtn
                onClick={() => window.open(invoice.file_url, '_blank', 'noopener,noreferrer')}
                className="!text-xs !px-3 !py-1.5"
              >
                View raw invoice
              </SecondaryBtn>
            )}
            <SecondaryBtn
              onClick={handleRerunParser}
              disabled={parserRunning}
              className="!text-xs !px-3 !py-1.5"
            >
              {parserRunning ? 'Running\u2026' : 'Re-run parser'}
            </SecondaryBtn>
          </div>

          {/* ── Vendor comparison strip ──────────────────────────────────── */}
          <VendorComparisonStrip
            comparison={vendorComparison}
            invoice={invoice}
          />
        </div>
      )}
    </SlideOverPanel>
  );
}

// VendorComparisonStrip — three rendering branches based on data shape:
//   1. Insufficient: < 3 prior invoices from this vendor → show explainer
//   2. Within: |delta| < 10% → "Within typical range — your average from
//      this vendor is $X across N invoices"
//   3. Compared: |delta| ≥ 10% → "X% above/below ($Y typical). Driver:
//      <item> $Z above prior avg from this vendor."
function VendorComparisonStrip({ comparison, invoice }) {
  if (!comparison) return null;

  const baseStyle = {
    background: 'var(--bg-subtle)',
    border: '1px solid var(--border)',
    borderRadius: 8,
    padding: '12px 14px',
  };

  if (comparison.kind === 'insufficient') {
    return (
      <div style={baseStyle}>
        <div style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text-secondary)', marginBottom: 4 }}>
          Vendor comparison
        </div>
        <div className="text-sm" style={{ color: 'var(--text-secondary)', lineHeight: 1.5 }}>
          {`Not enough history from ${invoice.vendor_name?.replace(/\n/g, ' \u00B7 ') || 'this vendor'} yet \u2014 need 3+ prior invoices to compare. (${comparison.priorCount} on file.)`}
        </div>
      </div>
    );
  }

  if (comparison.kind === 'within') {
    return (
      <div style={baseStyle}>
        <div style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text-secondary)', marginBottom: 4 }}>
          Vendor comparison
        </div>
        <div className="text-sm" style={{ color: 'var(--text-primary)', lineHeight: 1.5 }}>
          {`Within typical range \u2014 your average from this vendor is `}
          <strong style={{ fontVariantNumeric: 'tabular-nums' }}>{fmtMoneyShort(comparison.avg)}</strong>
          {` across ${comparison.priorCount} invoices.`}
        </div>
      </div>
    );
  }

  // 'compared' — outside threshold
  const direction = comparison.deltaPct >= 0 ? 'above' : 'below';
  const tone = comparison.deltaPct >= 0 ? 'critical' : 'success';
  const toneColor = tone === 'critical' ? 'var(--text-critical)' : 'var(--text-success)';
  const toneBg = tone === 'critical' ? 'var(--bg-critical-subtle)' : 'var(--bg-success-subtle)';

  return (
    <div style={{
      ...baseStyle,
      background: toneBg,
      borderColor: 'var(--border)',
      borderLeft: `3px solid ${toneColor}`,
    }}>
      <div style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', color: toneColor, marginBottom: 4 }}>
        Vendor comparison
      </div>
      <div className="text-sm" style={{ color: 'var(--text-primary)', lineHeight: 1.5 }}>
        {`This invoice is `}
        <strong style={{ color: toneColor, fontVariantNumeric: 'tabular-nums' }}>
          {`${Math.abs(comparison.deltaPct * 100).toFixed(0)}% ${direction}`}
        </strong>
        {` your avg from this vendor (${fmtMoneyShort(comparison.avg)} typical across ${comparison.priorCount} invoices).`}
      </div>
      {comparison.driver && (
        <div className="text-xs mt-1.5" style={{ color: 'var(--text-secondary)', lineHeight: 1.5 }}>
          {`Driver: `}
          <strong style={{ color: 'var(--text-primary)' }}>{comparison.driver.itemName}</strong>
          {` is `}
          <span style={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>
            {`${fmtMoneyShort(Math.abs(comparison.driver.delta))} ${comparison.driver.delta >= 0 ? 'above' : 'below'}`}
          </span>
          {` its typical line total from this vendor (${fmtMoneyShort(comparison.driver.priorAvg)} typical).`}
        </div>
      )}
    </div>
  );
}
